import React, { useEffect, useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { CPDropdown } from "./CPDropdown";
import { CPInput } from "./CPInput";
import { TaxLineEditor } from "./shared/TaxLineEditor";
import { AddInvoiceLineItemModal } from "./invoice/AddInvoiceLineItemModal";
import {
  formatInvoiceStatus,
  formatInvoiceUpdatedAt,
  parseInvoiceTaxLine,
  recomputeInvoiceTotals,
} from "../helpers/invoices";
import { getInvoiceReviewActions } from "../helpers/invoiceWorkflow";
import { formatCurrency } from "../helpers/estimateTotals";
import {
  computeReviewLineTotal,
  LINE_STATUS,
  moveInvoiceLineItem,
  PRICING_SOURCE,
  reindexInvoiceLineSortOrder,
} from "../helpers/invoiceReview";
import { getInvoiceStatusPillStyle } from "../helpers/invoiceStatusUi";
import { getTaxLineLabel } from "../helpers/estimateTax";
import { listFinanceAccounts } from "../helpers/financeAccounts";
import {
  buildPaymentTermsState,
  getOrganizationPaymentTermsConfig,
  isManualDueDatePreset,
  PAYMENT_TERMS_OPTIONS,
  resolveInvoiceDate,
  resolveInvoicePaymentTermsFields,
} from "../helpers/invoicePaymentTerms";
import {
  EMPTY_PAYMENT_INSTRUCTIONS,
  getOrganizationPaymentInstructions,
  normalizePaymentInstructions,
  resolveInvoicePaymentInstructions,
  validatePaymentInstructions,
} from "../helpers/invoicePaymentInstructions";
import {
  getOrganizationCustomerNotes,
  normalizeCustomerNotes,
  resolveInvoiceCustomerNotes,
} from "../helpers/invoiceCustomerNotes";
import {
  getOrganizationInvoiceFooter,
  normalizeInvoiceFooter,
  resolveInvoiceFooter,
} from "../helpers/invoiceFooter";
import { PaymentInstructionsFields } from "./invoice/PaymentInstructionsFields";
import {
  CP_MODAL_OVERLAY_STYLE,
  cpModalPanelStyle,
} from "./shared/crewpilotModalStyles";
import { ViewportDialog, ViewportPortal } from "./shared/ViewportDialog";
import { useOrganization } from "../context/OrganizationContext";
import { supabase } from "../supabase";
import { usePermissions } from "../hooks/usePermissions";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { InvoiceReviewLinesDesktop } from "./invoice/InvoiceReviewLinesDesktop";
import { InvoiceStatusPill } from "./invoice/invoiceDesktopParts";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import "./InvoiceReviewScreen.css";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "16px",
  marginBottom: "12px",
};

const ACTION_BTN = {
  padding: "10px 16px",
  borderRadius: "10px",
  border: "1px solid rgba(201,168,76,0.45)",
  background: "rgba(201,168,76,0.12)",
  color: "#c9a84c",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const DELETE_PILL_STYLE = {
  borderRadius: "999px",
  padding: "6px 12px",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  border: "1px solid rgba(224,107,90,0.45)",
  background: "rgba(224,107,90,0.12)",
  color: "#e06b5a",
  cursor: "pointer",
};

const ACTION_HELPER_STYLE = {
  margin: "-4px 0 0",
  padding: "0 4px",
  fontSize: "12px",
  lineHeight: 1.45,
  opacity: 0.62,
};

const LINE_TOOLBAR_STYLE = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "10px 14px",
  marginBottom: "14px",
  padding: "10px 12px",
  borderRadius: "12px",
  border: "1px solid rgba(201,168,76,0.22)",
  background: "rgba(0,0,0,0.18)",
};

const TOOLBAR_CHECKBOX_STYLE = {
  width: "16px",
  height: "16px",
  accentColor: "#c9a84c",
  cursor: "pointer",
};

const TOOLBAR_BTN = {
  ...ACTION_BTN,
  padding: "6px 12px",
  fontSize: "12px",
};

const TOOLBAR_BTN_DESTRUCTIVE = {
  ...TOOLBAR_BTN,
  borderColor: "rgba(224,107,90,0.45)",
  background: "rgba(224,107,90,0.12)",
  color: "#e06b5a",
};

function InvoiceActionGroup({ helper, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {children}
      {helper ? <p style={ACTION_HELPER_STYLE}>{helper}</p> : null}
    </div>
  );
}

const MODAL_OVERLAY_STYLE = CP_MODAL_OVERLAY_STYLE;
const MODAL_PANEL_STYLE = cpModalPanelStyle({ maxWidth: "440px" });

function formatPricingSource(source) {
  switch (source) {
    case PRICING_SOURCE.SEED_QUOTE_SNAPSHOT:
      return "Quote snapshot";
    case PRICING_SOURCE.SEED_CATALOG_SELL:
      return "Catalog sell";
    case PRICING_SOURCE.SEED_COST_CONTEXT:
      return "Cost context";
    case PRICING_SOURCE.MANUAL_OVERRIDE:
      return "Manual override";
    default:
      return source || "—";
  }
}

const LINE_FIELD_STYLE = {
  display: "block",
  width: "100%",
  marginTop: "4px",
  padding: "8px",
  borderRadius: "8px",
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(0,0,0,0.2)",
  color: "white",
  boxSizing: "border-box",
};

function ReviewLineRow({
  line,
  index,
  totalCount,
  selected,
  expanded,
  disabled,
  onToggleSelect,
  onToggleExpand,
  onChange,
  onRemoveFromDraft,
  onMove,
}) {
  const isRemoved = line.line_status === LINE_STATUS.REMOVED;
  const canMoveUp = !disabled && !isRemoved && index > 0;
  const canMoveDown = !disabled && !isRemoved && index < totalCount - 1;
  const showTaxable = line.line_type === "service";

  function updateLine(patch) {
    const next = { ...line, ...patch };
    if ("unit_amount" in patch || "discount_percent" in patch || "quantity" in patch) {
      next.line_total = computeReviewLineTotal(
        next.quantity,
        next.unit_amount,
        next.discount_percent,
      );
      if ("unit_amount" in patch) {
        next.pricing_source = PRICING_SOURCE.MANUAL_OVERRIDE;
      }
    }
    onChange(next);
  }

  function updateMetadata(patch) {
    updateLine({
      metadata: {
        ...(line.metadata || {}),
        ...patch,
      },
    });
  }

  return (
    <div
      style={{
        ...CARD_STYLE,
        opacity: isRemoved ? 0.55 : 1,
        borderColor: isRemoved
          ? "rgba(255,255,255,0.12)"
          : "rgba(201,168,76,0.25)",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "flex-start",
        }}
      >
        <input
          type="checkbox"
          checked={selected}
          disabled={disabled}
          onChange={onToggleSelect}
          style={{ marginTop: "4px" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              marginBottom: "10px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={{ fontSize: "12px", opacity: 0.8 }}>
                Name
                <input
                  type="text"
                  value={line.description ?? ""}
                  disabled={disabled || isRemoved}
                  onChange={(e) => updateLine({ description: e.target.value })}
                  style={{
                    ...LINE_FIELD_STYLE,
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                />
              </label>
              <div style={{ opacity: 0.65, fontSize: "12px", marginTop: "6px" }}>
                {line.line_type} · {formatPricingSource(line.pricing_source)}
                {isRemoved && (
                  <span style={{ opacity: 0.7 }}> (removed)</span>
                )}
              </div>
            </div>
            <div style={{ fontWeight: 700, color: "#c9a84c", flexShrink: 0 }}>
              {formatCurrency(line.line_total)}
            </div>
          </div>

          <label
            style={{
              fontSize: "12px",
              opacity: 0.8,
              display: "block",
              marginBottom: "10px",
            }}
          >
            Description
            <textarea
              value={line.detail ?? ""}
              disabled={disabled || isRemoved}
              onChange={(e) => updateLine({ detail: e.target.value || null })}
              rows={2}
              style={{
                ...LINE_FIELD_STYLE,
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
          </label>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <label style={{ fontSize: "12px", opacity: 0.8 }}>
              Qty
              <input
                type="number"
                min="0"
                step="0.01"
                value={line.quantity ?? ""}
                disabled={disabled || isRemoved}
                onChange={(e) =>
                  updateLine({ quantity: Number(e.target.value) || 0 })
                }
                style={LINE_FIELD_STYLE}
              />
            </label>
            <label style={{ fontSize: "12px", opacity: 0.8 }}>
              Unit
              <input
                type="text"
                value={line.unit_label ?? ""}
                disabled={disabled || isRemoved}
                onChange={(e) => updateLine({ unit_label: e.target.value })}
                style={LINE_FIELD_STYLE}
              />
            </label>
            <label style={{ fontSize: "12px", opacity: 0.8 }}>
              Bill rate
              <input
                type="number"
                min="0"
                step="0.01"
                value={line.unit_amount ?? ""}
                disabled={disabled || isRemoved}
                onChange={(e) =>
                  updateLine({ unit_amount: Number(e.target.value) || 0 })
                }
                style={LINE_FIELD_STYLE}
              />
            </label>
            <label style={{ fontSize: "12px", opacity: 0.8 }}>
              Discount %
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={line.discount_percent ?? 0}
                disabled={disabled || isRemoved}
                onChange={(e) =>
                  updateLine({ discount_percent: Number(e.target.value) || 0 })
                }
                style={LINE_FIELD_STYLE}
              />
            </label>
          </div>

          {showTaxable ? (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                opacity: 0.85,
                marginBottom: "10px",
              }}
            >
              <input
                type="checkbox"
                checked={line.metadata?.taxable !== false}
                disabled={disabled || isRemoved}
                onChange={(e) => updateMetadata({ taxable: e.target.checked })}
              />
              Taxable
            </label>
          ) : null}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              disabled={!canMoveUp}
              onClick={() => onMove?.("up")}
              style={{
                ...ACTION_BTN,
                padding: "6px 10px",
                fontSize: "12px",
                opacity: canMoveUp ? 1 : 0.45,
                cursor: canMoveUp ? "pointer" : "not-allowed",
              }}
            >
              Move Up
            </button>
            <button
              type="button"
              disabled={!canMoveDown}
              onClick={() => onMove?.("down")}
              style={{
                ...ACTION_BTN,
                padding: "6px 10px",
                fontSize: "12px",
                opacity: canMoveDown ? 1 : 0.45,
                cursor: canMoveDown ? "pointer" : "not-allowed",
              }}
            >
              Move Down
            </button>
            <button
              type="button"
              onClick={onToggleExpand}
              style={{
                ...ACTION_BTN,
                padding: "6px 10px",
                fontSize: "12px",
              }}
            >
              {expanded ? "Hide references" : "Show references"}
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={onRemoveFromDraft}
              style={{
                ...ACTION_BTN,
                padding: "6px 10px",
                fontSize: "12px",
                borderColor: "rgba(224,107,90,0.45)",
                color: "#e06b5a",
              }}
            >
              Remove from invoice
            </button>
          </div>

          {expanded && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                borderRadius: "10px",
                background: "rgba(0,0,0,0.25)",
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              <div>
                Cost reference: {formatCurrency(line.reference_unit_cost)}
              </div>
              <div>
                Quoted reference:{" "}
                {line.reference_quoted_unit_price != null
                  ? formatCurrency(line.reference_quoted_unit_price)
                  : "—"}
              </div>
              <div>
                Catalog sell reference:{" "}
                {line.reference_catalog_sell != null
                  ? formatCurrency(line.reference_catalog_sell)
                  : "—"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function InvoiceReviewScreen({
  invoice,
  saving,
  onBack,
  onSaveDraft,
  onReseedBillingPeriod,
  onPreviewInvoice,
  onMarkSent,
  onDeleteDraft,
}) {
  const isDesktop = useIsDesktopViewport();
  const { actor } = usePermissions();
  const { settings: orgSettings } = useOrganization();
  const actions = getInvoiceReviewActions(invoice?.status, {
    hasProject: Boolean(invoice?.project_id),
  });
  const statusLabel = formatInvoiceStatus(invoice?.status);
  const pillStyle = getInvoiceStatusPillStyle(invoice?.status);
  const orgPaymentInstructions = useMemo(
    () => getOrganizationPaymentInstructions(orgSettings),
    [orgSettings],
  );
  const orgPaymentTerms = useMemo(
    () => getOrganizationPaymentTermsConfig(orgSettings),
    [orgSettings],
  );
  const orgCustomerNotes = useMemo(
    () => getOrganizationCustomerNotes(orgSettings),
    [orgSettings],
  );
  const orgInvoiceFooter = useMemo(
    () => getOrganizationInvoiceFooter(orgSettings),
    [orgSettings],
  );

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [invoiceFooter, setInvoiceFooter] = useState("");
  const [clientReference, setClientReference] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [projectDiscountPercent, setProjectDiscountPercent] = useState("0");
  const [goodwillAdjustment, setGoodwillAdjustment] = useState("0");
  const [taxLine1, setTaxLine1] = useState(() => parseInvoiceTaxLine(null, 1));
  const [taxLine2, setTaxLine2] = useState(() => parseInvoiceTaxLine(null, 2));
  const [lineItems, setLineItems] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState({});
  const [expandedLineIds, setExpandedLineIds] = useState({});
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [batchDiscount, setBatchDiscount] = useState("");
  const [batchUnitAmount, setBatchUnitAmount] = useState("");
  const [depositAccountId, setDepositAccountId] = useState("");
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [paymentTermsPreset, setPaymentTermsPreset] = useState("net_30");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentInstructionsOverride, setPaymentInstructionsOverride] =
    useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState(() => ({
    ...EMPTY_PAYMENT_INSTRUCTIONS,
  }));
  const [paymentInstructionsErrors, setPaymentInstructionsErrors] = useState(
    {},
  );
  const [paymentTermsOverride, setPaymentTermsOverride] = useState(false);
  const [customerNotesOverride, setCustomerNotesOverride] = useState(false);
  const [invoiceFooterOverride, setInvoiceFooterOverride] = useState(false);
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (!invoice) return;
    setInvoiceNumber(invoice.invoice_number || "");
    setClientReference(invoice.client_reference || "");
    setReviewNotes(invoice.review_notes || "");
    setProjectDiscountPercent(String(invoice.project_discount_percent ?? 0));
    setGoodwillAdjustment(String(invoice.goodwill_adjustment ?? 0));
    setTaxLine1(parseInvoiceTaxLine(invoice, 1));
    setTaxLine2(parseInvoiceTaxLine(invoice, 2));
    setLineItems(invoice.lineItems || []);
    setDepositAccountId(invoice.deposit_account_id || "");

    const termsOverride = invoice.payment_terms_override === true;
    setPaymentTermsOverride(termsOverride);
    const paymentState = resolveInvoicePaymentTermsFields({
      invoice,
      organizationPaymentTerms: orgPaymentTerms,
    });
    setPaymentTermsPreset(paymentState.payment_terms_preset);
    setInvoiceDate(paymentState.invoice_date);
    setDueDate(paymentState.due_date);

    const notesOverride = invoice.customer_notes_override === true;
    setCustomerNotesOverride(notesOverride);
    setNotes(
      resolveInvoiceCustomerNotes({
        invoice,
        organizationCustomerNotes: orgCustomerNotes,
      }),
    );

    const footerOverride = invoice.invoice_footer_override === true;
    setInvoiceFooterOverride(footerOverride);
    setInvoiceFooter(
      resolveInvoiceFooter({
        invoice,
        organizationInvoiceFooter: orgInvoiceFooter,
      }),
    );

    const override = invoice.payment_instructions_override === true;
    setPaymentInstructionsOverride(override);
    setPaymentInstructions(
      resolveInvoicePaymentInstructions({
        invoice,
        organizationPaymentInstructions: orgPaymentInstructions,
      }),
    );
    setPaymentInstructionsErrors({});
    setSelectedLineIds({});
    setExpandedLineIds({});
  }, [
    invoice,
    orgPaymentInstructions,
    orgPaymentTerms,
    orgCustomerNotes,
    orgInvoiceFooter,
  ]);

  useEffect(() => {
    let cancelled = false;
    void listFinanceAccounts(supabase, { includeInactive: false })
      .then((rows) => {
        if (!cancelled) {
          setFinanceAccounts(rows || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFinanceAccounts([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rollups = useMemo(
    () =>
      recomputeInvoiceTotals(
        {
          project_discount_percent: Number(projectDiscountPercent) || 0,
          goodwill_adjustment: Number(goodwillAdjustment) || 0,
        },
        lineItems,
        [taxLine1, taxLine2],
      ),
    [lineItems, projectDiscountPercent, goodwillAdjustment, taxLine1, taxLine2],
  );

  const selectedIds = useMemo(
    () => Object.keys(selectedLineIds).filter((id) => selectedLineIds[id]),
    [selectedLineIds],
  );

  const allLinesSelected =
    lineItems.length > 0 &&
    lineItems.every((line) => selectedLineIds[line.line_id]);
  const someLinesSelected = selectedIds.length > 0 && !allLinesSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someLinesSelected;
    }
  }, [someLinesSelected]);

  function updateLineAt(index, nextLine) {
    setLineItems((prev) => prev.map((row, i) => (i === index ? nextLine : row)));
  }

  function moveLineAt(index, direction) {
    setLineItems((prev) => moveInvoiceLineItem(prev, index, direction));
  }

  function toggleSelectAll() {
    if (allLinesSelected) {
      setSelectedLineIds({});
      return;
    }
    const next = {};
    for (const line of lineItems) {
      next[line.line_id] = true;
    }
    setSelectedLineIds(next);
  }

  function removeSelectedLines() {
    if (!selectedIds.length) return;
    const removeSet = new Set(selectedIds);
    setLineItems((prev) =>
      reindexInvoiceLineSortOrder(
        prev.filter((line) => !removeSet.has(line.line_id)),
      ),
    );
    setSelectedLineIds({});
    setExpandedLineIds((prev) => {
      const next = { ...prev };
      for (const id of selectedIds) delete next[id];
      return next;
    });
  }

  function removeLineFromDraft(lineId) {
    if (!lineId) return;
    setLineItems((prev) =>
      reindexInvoiceLineSortOrder(
        prev.filter((line) => line.line_id !== lineId),
      ),
    );
    setSelectedLineIds((prev) => {
      if (!prev[lineId]) return prev;
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
    setExpandedLineIds((prev) => {
      if (!prev[lineId]) return prev;
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  }

  function handleAddLines(newLines) {
    if (!newLines?.length) return;
    setLineItems((prev) =>
      reindexInvoiceLineSortOrder([...prev, ...newLines]),
    );
  }

  function applyBatchEdit() {
    const discount = batchDiscount !== "" ? Number(batchDiscount) : null;
    const unitAmount = batchUnitAmount !== "" ? Number(batchUnitAmount) : null;

    setLineItems((prev) =>
      prev.map((line) => {
        if (!selectedIds.includes(line.line_id)) return line;
        const next = { ...line };
        if (discount != null && !Number.isNaN(discount)) {
          next.discount_percent = discount;
        }
        if (unitAmount != null && !Number.isNaN(unitAmount)) {
          next.unit_amount = unitAmount;
          next.pricing_source = PRICING_SOURCE.MANUAL_OVERRIDE;
        }
        next.line_total = computeReviewLineTotal(
          next.quantity,
          next.unit_amount,
          next.discount_percent,
        );
        return next;
      }),
    );
    setShowBatchModal(false);
    setBatchDiscount("");
    setBatchUnitAmount("");
  }

  async function handleSaveDraft() {
    const effectiveTerms = paymentTermsOverride
      ? buildPaymentTermsState({
          preset: paymentTermsPreset,
          invoiceDate,
          dueDate,
          previousDays: invoice?.payment_terms_days,
        })
      : resolveInvoicePaymentTermsFields({
          invoice: { ...invoice, payment_terms_override: false },
          organizationPaymentTerms: orgPaymentTerms,
          invoiceDate,
        });

    const effectiveNotes = customerNotesOverride
      ? normalizeCustomerNotes(notes)
      : orgCustomerNotes;

    const effectiveFooter = invoiceFooterOverride
      ? normalizeInvoiceFooter(invoiceFooter)
      : orgInvoiceFooter;

    const effectivePaymentInstructions = paymentInstructionsOverride
      ? paymentInstructions
      : orgPaymentInstructions;
    const validation = validatePaymentInstructions(
      effectivePaymentInstructions,
    );
    setPaymentInstructionsErrors(validation.errors);
    if (!validation.valid) {
      return;
    }

    await onSaveDraft?.({
      invoiceNumber,
      notes: effectiveNotes,
      clientReference,
      reviewNotes,
      projectDiscountPercent: Number(projectDiscountPercent) || 0,
      goodwillAdjustment: Number(goodwillAdjustment) || 0,
      taxLines: [taxLine1, taxLine2],
      lineItems: reindexInvoiceLineSortOrder(lineItems),
      depositAccountId: depositAccountId || null,
      invoiceDate: effectiveTerms.invoice_date,
      paymentTermsPreset: effectiveTerms.payment_terms_preset,
      paymentTermsDays: effectiveTerms.payment_terms_days,
      dueDate: effectiveTerms.due_date,
      paymentTermsOverride,
      paymentInstructions: validation.config,
      paymentInstructionsOverride,
      customerNotesOverride,
      invoiceFooter: effectiveFooter,
      invoiceFooterOverride,
    });
  }

  function handlePaymentInstructionsOverrideChange(checked) {
    setPaymentInstructionsOverride(checked);
    setPaymentInstructionsErrors({});
    if (checked) {
      setPaymentInstructions(
        normalizePaymentInstructions(
          paymentInstructions.preferredMethod || paymentInstructions.paymentEmail
            ? paymentInstructions
            : orgPaymentInstructions,
        ),
      );
    } else {
      setPaymentInstructions(
        normalizePaymentInstructions(orgPaymentInstructions),
      );
    }
  }

  function handlePaymentTermsOverrideChange(checked) {
    setPaymentTermsOverride(checked);
    if (checked) {
      const paymentState = buildPaymentTermsState({
        preset: paymentTermsPreset || orgPaymentTerms.preset,
        invoiceDate:
          invoiceDate ||
          resolveInvoiceDate(invoice, { fallbackToToday: true }),
        dueDate,
        previousDays: invoice?.payment_terms_days ?? orgPaymentTerms.days,
      });
      setPaymentTermsPreset(paymentState.payment_terms_preset);
      setInvoiceDate(paymentState.invoice_date);
      setDueDate(paymentState.due_date);
    } else {
      const paymentState = resolveInvoicePaymentTermsFields({
        invoice: { ...invoice, payment_terms_override: false },
        organizationPaymentTerms: orgPaymentTerms,
      });
      setPaymentTermsPreset(paymentState.payment_terms_preset);
      setInvoiceDate(paymentState.invoice_date);
      setDueDate(paymentState.due_date);
    }
  }

  function handleCustomerNotesOverrideChange(checked) {
    setCustomerNotesOverride(checked);
    if (checked) {
      setNotes(normalizeCustomerNotes(notes || orgCustomerNotes));
    } else {
      setNotes(normalizeCustomerNotes(orgCustomerNotes));
    }
  }

  function handleInvoiceFooterOverrideChange(checked) {
    setInvoiceFooterOverride(checked);
    if (checked) {
      setInvoiceFooter(
        normalizeInvoiceFooter(invoiceFooter || orgInvoiceFooter),
      );
    } else {
      setInvoiceFooter(normalizeInvoiceFooter(orgInvoiceFooter));
    }
  }

  function applyPaymentPreset(nextPreset) {
    const paymentState = buildPaymentTermsState({
      preset: nextPreset,
      invoiceDate,
      dueDate,
      previousDays: invoice?.payment_terms_days,
    });
    setPaymentTermsPreset(paymentState.payment_terms_preset);
    setInvoiceDate(paymentState.invoice_date);
    setDueDate(paymentState.due_date);
  }

  function applyInvoiceDate(nextInvoiceDate) {
    const paymentState = buildPaymentTermsState({
      preset: paymentTermsPreset,
      invoiceDate: nextInvoiceDate,
      dueDate,
      previousDays: invoice?.payment_terms_days,
    });
    setInvoiceDate(paymentState.invoice_date);
    setDueDate(paymentState.due_date);
  }

  const isManualDueDate = isManualDueDatePreset(paymentTermsPreset);
  const termsFieldsDisabled =
    saving || !actions.editLines || !paymentTermsOverride;

  if (!invoice) {
    return (
      <CrewPilotModuleShell
        title="Invoice Review"
        sectionLabel="INVOICES"
        onBack={onBack}
        desktopPage
      >
        <p style={{ opacity: 0.7 }}>Invoice not found.</p>
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Invoice Review"
      subtitle={invoice.title || "Draft invoice"}
      sectionLabel={isDesktop ? undefined : "INVOICES"}
      onBack={onBack}
      desktopBackLabel={isDesktop ? "Invoice Hub" : undefined}
      desktopPage={!isDesktop}
      layout={isDesktop ? PAGE_LAYOUT.COMMAND : undefined}
      compact={isDesktop}
      headerActions={
        isDesktop ? (
          <div className="inv-head-actions">
            <InvoiceStatusPill status={invoice.status} />
          </div>
        ) : undefined
      }
    >
      <div className={isDesktop ? "invoice-review inv-rev-desk" : "invoice-review"}>
        <div className="invoice-review__workspace">
          <div className="invoice-review__main">
      <div style={CARD_STYLE}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            alignItems: "flex-start",
            marginBottom: "12px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ opacity: 0.65, fontSize: "12px", marginBottom: "4px" }}>
              Invoice Number
            </div>
            <CPInput
              label=""
              value={invoiceNumber}
              onChange={setInvoiceNumber}
              placeholder="INV-..."
              disabled={!actions.editInvoiceNumber || saving}
            />
          </div>
          <div
            style={{
              ...pillStyle,
              borderRadius: "999px",
              padding: "6px 12px",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {statusLabel}
          </div>
        </div>

        <div style={{ opacity: 0.72, fontSize: "13px", lineHeight: 1.5, marginTop: "12px" }}>
          <div>Client: {invoice.clients?.client_name || "—"}</div>
          <div>Project: {invoice.title || "—"}</div>
          <div>Seeded: {formatInvoiceUpdatedAt(invoice.seeded_at)}</div>
          <div>Updated: {formatInvoiceUpdatedAt(invoice.updated_at)}</div>
        </div>

        <div style={{ marginTop: "14px" }}>
          <CPDropdown
            label="Deposit Account"
            value={depositAccountId}
            onChange={setDepositAccountId}
            disabled={saving}
            options={[
              {
                value: "",
                label: "Select where payment will be deposited",
              },
              ...financeAccounts.map((account) => ({
                value: account.id,
                label: account.name,
              })),
            ]}
          />
          <p
            style={{
              margin: "6px 0 0",
              fontSize: "12px",
              opacity: 0.55,
            }}
          >
            Set when the invoice is created. Used automatically when payment is
            received — no need to choose an account again at pay time.
          </p>
        </div>

        <div
          style={{
            marginTop: "18px",
            paddingTop: "16px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: "12px",
            }}
          >
            Payment Terms
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "14px",
              fontSize: "13px",
              cursor: actions.editLines && !saving ? "pointer" : "default",
            }}
          >
            <input
              type="checkbox"
              checked={paymentTermsOverride}
              disabled={saving || !actions.editLines}
              onChange={(event) =>
                handlePaymentTermsOverrideChange(event.target.checked)
              }
            />
            Override organization defaults for this invoice
          </label>

          <div style={{ marginBottom: "12px" }}>
            <CPDropdown
              label="Payment Terms"
              value={paymentTermsPreset}
              onChange={applyPaymentPreset}
              disabled={termsFieldsDisabled}
              includePlaceholder={false}
              options={PAYMENT_TERMS_OPTIONS}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "12px",
            }}
          >
            <label style={{ display: "block" }}>
              <div style={{ opacity: 0.65, fontSize: "12px", marginBottom: "6px" }}>
                Invoice Date
              </div>
              <input
                type="date"
                value={invoiceDate || ""}
                onChange={(event) => applyInvoiceDate(event.target.value)}
                disabled={termsFieldsDisabled}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: "rgba(0,0,0,0.28)",
                  color: "white",
                  padding: "10px 12px",
                  fontSize: "14px",
                }}
              />
            </label>

            <label style={{ display: "block" }}>
              <div style={{ opacity: 0.65, fontSize: "12px", marginBottom: "6px" }}>
                Due Date
              </div>
              <input
                type="date"
                value={dueDate || ""}
                onChange={(event) => setDueDate(event.target.value)}
                disabled={termsFieldsDisabled || !isManualDueDate}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: isManualDueDate
                    ? "rgba(0,0,0,0.28)"
                    : "rgba(255,255,255,0.04)",
                  color: "white",
                  padding: "10px 12px",
                  fontSize: "14px",
                  opacity: isManualDueDate ? 1 : 0.75,
                }}
              />
            </label>
          </div>
          <p
            style={{
              margin: "8px 0 0",
              fontSize: "12px",
              opacity: 0.55,
              lineHeight: 1.45,
            }}
          >
            {isManualDueDate
              ? "Choose the due date manually. This drives future receivables aging and Expected Cash."
              : "Due date updates automatically from the invoice date and selected terms."}
          </p>
        </div>

        {actions.deleteDraft && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "12px",
            }}
          >
            <button
              type="button"
              style={{
                ...DELETE_PILL_STYLE,
                opacity: saving || deleting ? 0.55 : 1,
                cursor: saving || deleting ? "not-allowed" : "pointer",
              }}
              disabled={saving || deleting}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Draft
            </button>
          </div>
        )}
      </div>

      <div style={CARD_STYLE}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <div style={{ fontWeight: 700 }}>Line Items</div>
          {actions.batchEdit && selectedIds.length > 0 && (
            <button
              type="button"
              style={{ ...ACTION_BTN, padding: "6px 12px", fontSize: "12px" }}
              onClick={() => setShowBatchModal(true)}
            >
              Batch edit ({selectedIds.length})
            </button>
          )}
        </div>

        {actions.editLines && (
          <div style={LINE_TOOLBAR_STYLE}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: lineItems.length === 0 ? 0.55 : 1,
              }}
            >
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allLinesSelected}
                disabled={saving || lineItems.length === 0}
                onChange={toggleSelectAll}
                style={TOOLBAR_CHECKBOX_STYLE}
              />
              Select All
            </label>

            <span style={{ fontSize: "13px", opacity: 0.75 }}>
              Selected: {selectedIds.length}
            </span>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginLeft: "auto" }}>
              <button
                type="button"
                style={{
                  ...TOOLBAR_BTN_DESTRUCTIVE,
                  opacity: saving || selectedIds.length === 0 ? 0.5 : 1,
                  cursor:
                    saving || selectedIds.length === 0
                      ? "not-allowed"
                      : "pointer",
                }}
                disabled={saving || selectedIds.length === 0}
                onClick={removeSelectedLines}
              >
                Remove Selected
              </button>
              <button
                type="button"
                style={{
                  ...TOOLBAR_BTN,
                  opacity: saving ? 0.5 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
                disabled={saving}
                onClick={() => setShowAddLineModal(true)}
              >
                Add Line Item
              </button>
            </div>
          </div>
        )}

        {lineItems.length === 0 ? (
          <p style={{ opacity: 0.7, margin: 0 }}>
            No line items yet. Add a service, or import labour and materials from
            the project.
          </p>
        ) : isDesktop ? (
          <InvoiceReviewLinesDesktop
            lines={lineItems}
            disabled={saving || !actions.editLines}
            selectedIds={selectedLineIds}
            expandedIds={expandedLineIds}
            allSelected={allLinesSelected}
            onToggleAll={toggleSelectAll}
            onToggleSelect={(lineId) =>
              setSelectedLineIds((prev) => ({ ...prev, [lineId]: !prev[lineId] }))
            }
            onToggleExpand={(lineId) =>
              setExpandedLineIds((prev) => ({ ...prev, [lineId]: !prev[lineId] }))
            }
            onChange={(line, nextLine) =>
              updateLineAt(
                lineItems.findIndex((row) => row.line_id === line.line_id),
                nextLine,
              )
            }
            onRemove={removeLineFromDraft}
            onMove={(line, direction) =>
              moveLineAt(
                lineItems.findIndex((row) => row.line_id === line.line_id),
                direction,
              )
            }
          />
        ) : (
          lineItems.map((line, index) => (
            <ReviewLineRow
              key={line.line_id}
              line={line}
              index={index}
              totalCount={lineItems.length}
              selected={!!selectedLineIds[line.line_id]}
              expanded={!!expandedLineIds[line.line_id]}
              disabled={saving || !actions.editLines}
              onToggleSelect={() =>
                setSelectedLineIds((prev) => ({
                  ...prev,
                  [line.line_id]: !prev[line.line_id],
                }))
              }
              onToggleExpand={() =>
                setExpandedLineIds((prev) => ({
                  ...prev,
                  [line.line_id]: !prev[line.line_id],
                }))
              }
              onChange={(nextLine) => updateLineAt(index, nextLine)}
              onRemoveFromDraft={() => removeLineFromDraft(line.line_id)}
              onMove={(direction) => moveLineAt(index, direction)}
            />
          ))
        )}
      </div>

      <div className="invoice-review__secondary">
      <div style={CARD_STYLE} className="invoice-review__adjustments">
        <div style={{ fontWeight: 700, marginBottom: "12px" }}>Adjustments</div>
        <CPInput
          label="Project discount %"
          value={projectDiscountPercent}
          onChange={setProjectDiscountPercent}
          placeholder="0"
          disabled={!actions.editProjectDiscount || saving}
        />
        <CPInput
          label="Goodwill adjustment (+/-)"
          value={goodwillAdjustment}
          onChange={setGoodwillAdjustment}
          placeholder="0.00"
          disabled={!actions.editGoodwill || saving}
        />
        <CPInput
          label="Internal review notes"
          value={reviewNotes}
          onChange={setReviewNotes}
          placeholder="Manager notes (not on client PDF)"
          disabled={!actions.editReviewNotes || saving}
        />
      </div>

      <div className="invoice-review__tax">
      <div style={{ marginTop: "8px", marginBottom: "8px", fontWeight: 700 }}>
        Tax Summary
      </div>
      <TaxLineEditor
        lineNumber={1}
        line={taxLine1}
        onChange={setTaxLine1}
        disabled={!actions.editTax || saving}
      />
      <TaxLineEditor
        lineNumber={2}
        line={taxLine2}
        onChange={setTaxLine2}
        disabled={!actions.editTax || saving}
      />
      </div>
      </div>
          </div>

          <aside className="invoice-review__aside">
      <div style={CARD_STYLE} className="invoice-review__totals-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span>Labour</span>
          <span>{formatCurrency(rollups.subtotalLabour)}</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span>Materials</span>
          <span>{formatCurrency(rollups.subtotalMaterials)}</span>
        </div>
        {(Number(rollups.subtotalServices) > 0 ||
          lineItems.some((line) => line.line_type === "service")) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Services</span>
            <span>{formatCurrency(rollups.subtotalServices)}</span>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
            opacity: 0.85,
          }}
        >
          <span>Line subtotal</span>
          <span>{formatCurrency(rollups.lineSubtotal)}</span>
        </div>
        {Number(projectDiscountPercent) > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
              opacity: 0.85,
            }}
          >
            <span>After project discount</span>
            <span>{formatCurrency(rollups.afterProjectDiscount)}</span>
          </div>
        )}
        {rollups.taxSummary.lines.map((line) => (
          <div
            key={`${line.type}-${line.label}`}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
              marginLeft: "16px",
              opacity: 0.9,
            }}
          >
            <span>
              {getTaxLineLabel(line)} ({line.rate}%)
            </span>
            <span>{formatCurrency(line.amount)}</span>
          </div>
        ))}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 700,
            color: "#c9a84c",
            marginTop: "8px",
          }}
        >
          <span>Grand Total</span>
          <span>{formatCurrency(rollups.totalAmount)}</span>
        </div>
      </div>

      <div style={CARD_STYLE}>
        <div
          style={{
            fontWeight: 700,
            fontSize: "13px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            opacity: 0.75,
            marginBottom: "10px",
          }}
        >
          How to Pay
        </div>
        <p
          style={{
            margin: "0 0 12px",
            fontSize: "12px",
            opacity: 0.55,
            lineHeight: 1.4,
          }}
        >
          Shown on the client invoice Preview and PDF. Uses organization
          defaults unless overridden for this invoice.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "14px",
            fontSize: "13px",
            cursor: actions.editNotes && !saving ? "pointer" : "default",
          }}
        >
          <input
            type="checkbox"
            checked={paymentInstructionsOverride}
            disabled={saving || !actions.editNotes}
            onChange={(event) =>
              handlePaymentInstructionsOverrideChange(event.target.checked)
            }
          />
          Override organization defaults for this invoice
        </label>

        <PaymentInstructionsFields
          value={paymentInstructions}
          onChange={(next) => {
            setPaymentInstructions(next);
            setPaymentInstructionsErrors({});
          }}
          errors={paymentInstructionsErrors}
          disabled={
            saving || !actions.editNotes || !paymentInstructionsOverride
          }
        />
      </div>

      <div style={CARD_STYLE}>
        <CPInput
          label="Client Reference"
          value={clientReference}
          onChange={setClientReference}
          placeholder="PO / work order (optional)"
          disabled={!actions.editClientReference || saving}
        />

        <div style={{ marginTop: "14px" }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: "10px",
            }}
          >
            Customer Notes
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
              fontSize: "13px",
              cursor: actions.editNotes && !saving ? "pointer" : "default",
            }}
          >
            <input
              type="checkbox"
              checked={customerNotesOverride}
              disabled={saving || !actions.editNotes}
              onChange={(event) =>
                handleCustomerNotesOverrideChange(event.target.checked)
              }
            />
            Override organization defaults for this invoice
          </label>
          <CPInput
            label=""
            value={notes}
            onChange={setNotes}
            placeholder="Thank you for your business (optional)"
            disabled={saving || !actions.editNotes || !customerNotesOverride}
          />
        </div>

        <div style={{ marginTop: "14px" }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: "10px",
            }}
          >
            Invoice Footer
          </div>
          <p
            style={{
              margin: "0 0 12px",
              fontSize: "12px",
              opacity: 0.55,
              lineHeight: 1.4,
            }}
          >
            Closing text at the bottom of the customer invoice. Leave blank to
            omit.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
              fontSize: "13px",
              cursor: actions.editNotes && !saving ? "pointer" : "default",
            }}
          >
            <input
              type="checkbox"
              checked={invoiceFooterOverride}
              disabled={saving || !actions.editNotes}
              onChange={(event) =>
                handleInvoiceFooterOverrideChange(event.target.checked)
              }
            />
            Override organization defaults for this invoice
          </label>
          <CPInput
            label=""
            value={invoiceFooter}
            onChange={setInvoiceFooter}
            placeholder={
              "Questions about this invoice?\nContact us at info@example.com"
            }
            disabled={saving || !actions.editNotes || !invoiceFooterOverride}
          />
        </div>
      </div>

      <div className="invoice-review__aside-actions">
        <InvoiceActionGroup>
          <button
            type="button"
            className="nx-btn nx-btn-gold"
            style={ACTION_BTN}
            disabled={saving}
            onClick={handleSaveDraft}
          >
            {saving ? "Saving…" : "Save Draft"}
          </button>
        </InvoiceActionGroup>

        {actions.reseedBillingPeriod && (
          <InvoiceActionGroup helper="Imports any uninvoiced labour and materials from the project into this invoice.">
            <button
              type="button"
              className="nx-btn"
              style={ACTION_BTN}
              disabled={saving}
              onClick={onReseedBillingPeriod}
            >
              Refresh from Project
            </button>
          </InvoiceActionGroup>
        )}

        {actions.previewInvoice && (
          <InvoiceActionGroup helper="Review the invoice layout and export a PDF when ready.">
            <button
              type="button"
              className="nx-btn"
              style={ACTION_BTN}
              onClick={onPreviewInvoice}
            >
              Preview Invoice
            </button>
          </InvoiceActionGroup>
        )}

        {actions.markSent && (
          <InvoiceActionGroup helper="Finalizes the invoice and records project allocations.">
            <button
              type="button"
              className="nx-btn nx-btn-gold"
              style={ACTION_BTN}
              disabled={saving || deleting}
              onClick={() => setShowSendConfirm(true)}
            >
              Send Invoice
            </button>
          </InvoiceActionGroup>
        )}
      </div>
          </aside>
        </div>

      {showDeleteConfirm && (
        <ViewportPortal>
        <div style={MODAL_OVERLAY_STYLE}>
          <div style={MODAL_PANEL_STYLE}>
            <h3 style={{ marginTop: 0 }}>Delete Draft?</h3>
            <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
              This draft invoice and its line items will be permanently removed.
              This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nx-btn"
                style={{
                  flex: 1,
                  borderColor: "rgba(224,107,90,0.45)",
                  color: "#e06b5a",
                }}
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await onDeleteDraft?.();
                    setShowDeleteConfirm(false);
                  } catch {
                    // Parent handlers surface errors.
                  } finally {
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? "Deleting…" : "Delete Draft"}
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      )}

      {showSendConfirm && (
        <ViewportPortal>
        <div style={MODAL_OVERLAY_STYLE}>
          <div style={MODAL_PANEL_STYLE}>
            <h3 style={{ marginTop: 0 }}>Send Invoice?</h3>
            <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
              Sending the invoice locks project allocations and marks the invoice
              as Sent.
            </p>
            <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => setShowSendConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                style={{ flex: 1 }}
                disabled={saving}
                onClick={async () => {
                  try {
                    await handleSaveDraft();
                    await onMarkSent?.();
                    setShowSendConfirm(false);
                  } catch {
                    // Parent handlers surface errors.
                  }
                }}
              >
                {saving ? "Sending…" : "Send Invoice"}
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      )}

      {showBatchModal && (
        <ViewportDialog
          presentation="sheet"
          label="Batch edit lines"
          onClose={() => setShowBatchModal(false)}
        >
            <h3 style={{ marginTop: 0 }}>Batch edit lines</h3>
            <p style={{ opacity: 0.8 }}>
              Apply to {selectedIds.length} selected line
              {selectedIds.length === 1 ? "" : "s"}.
            </p>
            <CPInput
              label="Discount % (optional)"
              value={batchDiscount}
              onChange={setBatchDiscount}
              placeholder="e.g. 10"
            />
            <CPInput
              label="Bill rate (optional)"
              value={batchUnitAmount}
              onChange={setBatchUnitAmount}
              placeholder="Unit amount"
            />
            <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              <button
                type="button"
                className="nx-btn"
                style={{ flex: 1 }}
                onClick={() => setShowBatchModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                style={{ flex: 1 }}
                onClick={applyBatchEdit}
              >
                Apply
              </button>
            </div>
        </ViewportDialog>
      )}

      <AddInvoiceLineItemModal
        open={showAddLineModal}
        actor={actor}
        invoice={invoice}
        lineItems={lineItems}
        onClose={() => setShowAddLineModal(false)}
        onAddLines={handleAddLines}
      />
      </div>
    </CrewPilotModuleShell>
  );
}
